import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { logActivity } from '../services/activities';
import { localDate } from '../utils/date';

const ACTIVITY_TYPES = [
  'Run',
  'Ride',
  'Walk',
  'Swim',
  'Weight Training',
  'HIIT',
  'Yoga',
  'Pilates',
  'Hike',
  'Other',
];

export function LogActivity() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    activity_date: localDate(),
    type: 'Run',
    duration_minutes: '',
    distance_miles: '',
    intensity: 'moderate' as 'low' | 'moderate' | 'high',
  });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.duration_minutes) return;
    setSaving(true);
    setError(null);
    try {
      await logActivity({
        activity_date: form.activity_date,
        type: form.type,
        duration_minutes: Number(form.duration_minutes),
        distance_miles: form.distance_miles ? Number(form.distance_miles) : null,
        intensity: form.intensity,
      });
      // Refresh the training week everywhere it is displayed
      await queryClient.invalidateQueries('daily-stats');
      setSaved(true);
      setForm((f) => ({ ...f, duration_minutes: '', distance_miles: '' }));
      setTimeout(() => {
        setSaved(false);
        setOpen(false);
      }, 1200);
    } catch (err) {
      console.error('Failed to log activity:', err);
      setError('Could not save the activity. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-secondary w-full">
        <Plus className="h-4 w-4" />
        Log an activity manually
      </button>
    );
  }

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">Log an activity</h3>
        <button onClick={() => setOpen(false)} className="btn-ghost px-2" aria-label="Close">
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && <p className="mb-3 text-sm text-rose-600 dark:text-rose-400">{error}</p>}

      <form onSubmit={submit} className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        <select
          value={form.type}
          onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          className="input select col-span-2 sm:col-span-1"
          aria-label="Activity type"
        >
          {ACTIVITY_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <input
          type="date"
          value={form.activity_date}
          max={localDate()}
          onChange={(e) => setForm((f) => ({ ...f, activity_date: e.target.value }))}
          className="input"
          aria-label="Date"
        />
        <input
          type="number"
          value={form.duration_minutes}
          onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))}
          className="input"
          placeholder="Minutes"
          min="1"
          required
        />
        <input
          type="number"
          value={form.distance_miles}
          onChange={(e) => setForm((f) => ({ ...f, distance_miles: e.target.value }))}
          className="input"
          placeholder="Miles (opt.)"
          min="0"
          step="0.1"
        />
        <select
          value={form.intensity}
          onChange={(e) => setForm((f) => ({ ...f, intensity: e.target.value as typeof form.intensity }))}
          className="input select"
          aria-label="Intensity"
        >
          <option value="low">Easy</option>
          <option value="moderate">Moderate</option>
          <option value="high">Hard</option>
        </select>
        <button type="submit" disabled={saving || !form.duration_minutes} className="btn-primary">
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : saved ? (
            <Check className="h-4 w-4" />
          ) : (
            'Save'
          )}
        </button>
      </form>
      <p className="mt-2 text-[11px] text-zinc-400">
        Manual entries count toward your training week and inform tomorrow&apos;s recommendation.
      </p>
    </div>
  );
}
